import {
	BACKDROP_WIDTH,
	BAT_BOB,
	BAT_FLIGHT_TICKS,
	BAT_GAP_MAX,
	BAT_GAP_MIN,
	BAT_POINTS,
	BAT_RADIUS,
	BAT_SKY_BOTTOM,
	BAT_SKY_TOP,
	BAT_SWOOPS,
	COUNTDOWN_TICKS,
	DEFAULT_DIFFICULTY,
	DEFAULT_EXTRAS,
	DEFAULT_ROUND_SECONDS,
	DEFAULT_STREAK_MODE,
	GHOST_CATCH_PEEK,
	GHOST_FIRST_TICKS,
	GHOST_GAP_MAX,
	GHOST_GAP_MIN,
	GHOST_OUT_TICKS,
	GHOST_POINTS,
	GHOST_SLIDE_TICKS,
	GOLDEN_ONE_IN,
	GOLDEN_POINTS,
	LIGHT_COLOR_COUNT,
	MISS_PENALTY,
	NIGHTMARE_GAP_MIN,
	NIGHTMARE_GAP_START,
	NIGHTMARE_LIFE_MIN,
	NIGHTMARE_LIFE_START,
	NIGHTMARE_LIT_EVERY_TICKS,
	NIGHTMARE_LIT_MAX,
	NIGHTMARE_LIT_START,
	NIGHTMARE_LIVES,
	NIGHTMARE_RAMP_TICKS,
	PACE,
	POINTS,
	SPOT_REST_TICKS,
	streakBonus,
	TICK_MS,
	TRICK_ONE_IN,
	TRICK_PENALTY,
} from "./constants";
import { HIDES } from "./hides";
import { sin } from "./math/det-math";
import { createRng, nextInt } from "./math/rng";
import { SPOTS } from "./spots";
import type {
	Bat,
	ControlState,
	Ghost,
	Light,
	RoundOptions,
	SimEvent,
	Tap,
	WorldState,
} from "./state";

export function createWorld(
	seed: number,
	seconds: number = DEFAULT_ROUND_SECONDS,
	options: RoundOptions = {},
): WorldState {
	const nightmare = options.nightmare ?? false;
	return {
		tick: 0,
		rng: createRng(seed),
		phase: "countdown",
		seconds,
		difficulty: options.difficulty ?? DEFAULT_DIFFICULTY,
		missPenalty: nightmare ? false : (options.missPenalty ?? false),
		streakMode: nightmare ? "strict" : (options.streakMode ?? DEFAULT_STREAK_MODE),
		nightmare,
		lives: nightmare ? NIGHTMARE_LIVES : 0,
		startTick: COUNTDOWN_TICKS,
		endTick: nightmare ? Number.MAX_SAFE_INTEGER : COUNTDOWN_TICKS + secondsToTicks(seconds),
		nextId: 1,
		nextLightTick: COUNTDOWN_TICKS,
		lights: [],
		restUntil: SPOTS.map(() => 0),
		score: 0,
		collected: 0,
		golden: 0,
		missed: 0,
		streak: 0,
		longestStreak: 0,
		expired: 0,
		// Mardröm always has both, so all runs compare fairly.
		extras: nightmare ? DEFAULT_EXTRAS : (options.extras ?? DEFAULT_EXTRAS),
		bat: null,
		nextBatTick: COUNTDOWN_TICKS + BAT_GAP_MIN,
		batsCaught: 0,
		ghost: null,
		nextGhostTick: COUNTDOWN_TICKS + GHOST_FIRST_TICKS,
		ghostsCaught: 0,
		tricked: 0,
	};
}

export function secondsToTicks(seconds: number): number {
	return Math.round((seconds * 1000) / TICK_MS);
}

/**
 * Advances the world by one tick (TICK_MS) and returns what happened. Mutates `world` in
 * place. Same state + same controls = same result on every engine: the seeded RNG for chance,
 * no engine-dependent maths, and arrays in a fixed order.
 */
export function step(world: WorldState, controls: Readonly<ControlState>): SimEvent[] {
	if (world.phase === "over") return [];
	const events: SimEvent[] = [];
	const t = ++world.tick;

	if (world.phase === "countdown") {
		if (t < world.startTick) return events;
		world.phase = "playing";
		events.push({ type: "started" });
	}

	// Taps first: a light tapped on its last tick still counts.
	for (const tap of controls.taps) {
		// A lit light before the ghost: a ghost must never take a tap meant for a light.
		if (catchBat(world, tap, events) || collect(world, tap, events)) continue;
		if (!catchGhost(world, tap, events)) miss(world, tap, events);
	}

	const kept: Light[] = [];
	for (const light of world.lights) {
		if (light.outTick <= t) {
			world.restUntil[light.spot] = t + SPOT_REST_TICKS;
			events.push({ type: "expired", light });
			if (!light.trick) {
				world.expired++;
				loseLife(world, events);
				if (world.streakMode === "strict") breakStreak(world, events);
			}
		} else {
			kept.push(light);
		}
	}
	world.lights = kept;

	if (world.bat && t >= world.bat.endTick) {
		events.push({ type: "batGone", bat: world.bat });
		world.bat = null;
	}

	if (world.ghost && t >= world.ghost.endTick) {
		events.push({ type: "ghostGone", ghost: world.ghost });
		world.ghost = null;
	}

	if (t >= world.endTick || (world.nightmare && world.lives === 0)) {
		world.phase = "over";
		world.lights = [];
		world.bat = null;
		world.ghost = null;
		events.push({ type: "timeUp", score: world.score });
		return events;
	}

	// A bat only when it can cross before the round ends.
	const bats = world.extras === "both" || world.extras === "bats";
	if (bats && !world.bat && t >= world.nextBatTick && world.endTick - t > BAT_FLIGHT_TICKS) {
		const leftToRight = nextInt(world.rng, 2) === 0;
		const bat: Bat = {
			id: world.nextId++,
			startTick: t,
			endTick: t + BAT_FLIGHT_TICKS,
			fromX: leftToRight ? -BAT_RADIUS : BACKDROP_WIDTH + BAT_RADIUS,
			toX: leftToRight ? BACKDROP_WIDTH + BAT_RADIUS : -BAT_RADIUS,
			y: BAT_SKY_TOP + nextInt(world.rng, BAT_SKY_BOTTOM - BAT_SKY_TOP + 1),
		};
		world.bat = bat;
		world.nextBatTick =
			bat.endTick + BAT_GAP_MIN + nextInt(world.rng, BAT_GAP_MAX - BAT_GAP_MIN + 1);
		events.push({ type: "batCame", bat });
	}

	// A ghost likewise only when it can hide again before the round ends.
	const ghostTicks = GHOST_SLIDE_TICKS * 2 + GHOST_OUT_TICKS;
	const ghosts = world.extras === "both" || world.extras === "ghosts";
	if (ghosts && !world.ghost && t >= world.nextGhostTick && world.endTick - t > ghostTicks) {
		const ghost: Ghost = {
			id: world.nextId++,
			hide: nextInt(world.rng, HIDES.length),
			startTick: t,
			endTick: t + ghostTicks,
		};
		world.ghost = ghost;
		world.nextGhostTick =
			ghost.endTick + GHOST_GAP_MIN + nextInt(world.rng, GHOST_GAP_MAX - GHOST_GAP_MIN + 1);
		events.push({ type: "ghostCame", ghost });
	}

	if (t >= world.nextLightTick && world.lights.length < litLimit(world)) {
		const light = switchOn(world);
		if (light) {
			events.push({ type: "lit", light });
			const { gapMin, gapMax } = lightGap(world);
			world.nextLightTick = t + gapMin + nextInt(world.rng, gapMax - gapMin + 1);
		}
	}
	return events;
}

/** How far into the round we are: 0 at the first light, 1 at the end. */
export function progress(world: WorldState): number {
	const p = (world.tick - world.startTick) / (world.endTick - world.startTick);
	return Math.min(1, Math.max(0, p));
}

/** Lights allowed on at once now: the pace's litStart … litEnd in equal parts of the round. */
export function litLimit(world: WorldState): number {
	if (world.nightmare) {
		const more = Math.floor(aliveTicks(world) / NIGHTMARE_LIT_EVERY_TICKS);
		return Math.min(NIGHTMARE_LIT_MAX, NIGHTMARE_LIT_START + more);
	}
	const { litStart, litEnd } = PACE[world.difficulty];
	const steps = litEnd - litStart + 1;
	return Math.min(litEnd, litStart + Math.floor(progress(world) * steps));
}

/** Ticks a light switched on now stays on. */
export function lightLife(world: WorldState): number {
	if (world.nightmare) {
		const span = NIGHTMARE_LIFE_START - NIGHTMARE_LIFE_MIN;
		return Math.round(NIGHTMARE_LIFE_MIN + span * nightmarePace(world));
	}
	const { lifeStart, lifeEnd } = PACE[world.difficulty];
	return Math.round(lifeStart + (lifeEnd - lifeStart) * progress(world));
}

/** Ticks since the first light could come (Mardröm's time alive). */
export function aliveTicks(world: WorldState): number {
	return Math.max(0, world.tick - world.startTick);
}

/** Mardröm's pace: 1 at the start, halving every RAMP_TICKS' worth of time, never reaching 0. */
function nightmarePace(world: WorldState): number {
	return NIGHTMARE_RAMP_TICKS / (NIGHTMARE_RAMP_TICKS + aliveTicks(world));
}

/** Ticks between two lights switching on now. */
function lightGap(world: WorldState): { gapMin: number; gapMax: number } {
	if (!world.nightmare) return PACE[world.difficulty];
	const gapMax = Math.round(
		NIGHTMARE_GAP_MIN + (NIGHTMARE_GAP_START - NIGHTMARE_GAP_MIN) * nightmarePace(world),
	);
	return { gapMin: NIGHTMARE_GAP_MIN, gapMax };
}

function loseLife(world: WorldState, events: SimEvent[]): void {
	if (!world.nightmare || world.lives === 0) return;
	world.lives--;
	events.push({ type: "lifeLost", lives: world.lives });
}

/** Ticks left of the round (0 during the countdown counts the whole round). */
export function ticksLeft(world: WorldState): number {
	return Math.max(0, world.endTick - Math.max(world.tick, world.startTick));
}

/** Collects the lit light closest to the tap, if the tap is within its reach; false if none. */
function collect(world: WorldState, tap: Tap, events: SimEvent[]): boolean {
	let best = -1;
	let bestDist = Number.POSITIVE_INFINITY;
	world.lights.forEach((light, i) => {
		const spot = SPOTS[light.spot];
		if (!spot) return;
		const dx = tap.x - spot.x;
		const dy = tap.y - spot.y;
		const dist = dx * dx + dy * dy;
		const reach = spot.r + tap.reach;
		if (dist <= reach * reach && dist < bestDist) {
			best = i;
			bestDist = dist;
		}
	});
	const light = world.lights[best];
	if (!light) return false;
	world.lights.splice(best, 1);
	world.restUntil[light.spot] = world.tick + SPOT_REST_TICKS;
	// Something new soon, but not in the same instant.
	world.nextLightTick = Math.max(world.nextLightTick, world.tick + lightGap(world).gapMin);
	if (light.trick) {
		const penalty = Math.min(TRICK_PENALTY, world.score);
		world.score -= penalty;
		world.tricked++;
		loseLife(world, events);
		breakStreak(world, events);
		events.push({ type: "tricked", light, penalty, score: world.score });
		return true;
	}
	const points = light.golden ? GOLDEN_POINTS : POINTS;
	world.score += points;
	world.collected++;
	if (light.golden) world.golden++;
	events.push({ type: "collected", light, points, score: world.score });
	addToStreak(world, events);
	return true;
}

/** One more collect in a row (Svit on); every STREAK_EVERY gives a growing bonus. */
function addToStreak(world: WorldState, events: SimEvent[]): void {
	if (world.streakMode === "off") return;
	world.streak++;
	world.longestStreak = Math.max(world.longestStreak, world.streak);
	const bonus = streakBonus(world.streak);
	if (bonus === 0) return;
	world.score += bonus;
	events.push({ type: "streak", count: world.streak, bonus, score: world.score });
}

function breakStreak(world: WorldState, events: SimEvent[]): void {
	if (world.streak === 0) return;
	events.push({ type: "streakBroken", streak: world.streak });
	world.streak = 0;
}

/** Where a bat is at a (possibly fractional) tick: across the picture, swooping up and down. */
export function batPosition(bat: Bat, tick: number): { x: number; y: number } {
	const p = Math.min(1, Math.max(0, (tick - bat.startTick) / (bat.endTick - bat.startTick)));
	return {
		x: bat.fromX + (bat.toX - bat.fromX) * p,
		y: bat.y + BAT_BOB * sin(p * BAT_SWOOPS * 2 * Math.PI),
	};
}

/** Catches the bat if the tap is within its reach. */
function catchBat(world: WorldState, tap: Tap, events: SimEvent[]): boolean {
	const bat = world.bat;
	if (!bat) return false;
	const { x, y } = batPosition(bat, world.tick);
	const dx = tap.x - x;
	const dy = tap.y - y;
	const reach = BAT_RADIUS + tap.reach;
	if (dx * dx + dy * dy > reach * reach) return false;
	world.bat = null;
	world.score += BAT_POINTS;
	world.batsCaught++;
	events.push({ type: "batCaught", bat, x, y, points: BAT_POINTS, score: world.score });
	addToStreak(world, events);
	return true;
}

/** Where a ghost is at a (possibly fractional) tick, and how far out (0 hidden, 1 fully out). */
export function ghostPosition(ghost: Ghost, tick: number): { x: number; y: number; peek: number } {
	const hide = HIDES[ghost.hide];
	if (!hide) return { x: 0, y: 0, peek: 0 };
	const edge = Math.min(tick - ghost.startTick, ghost.endTick - tick) / GHOST_SLIDE_TICKS;
	const p = Math.min(1, Math.max(0, edge));
	const peek = p * p * (3 - 2 * p);
	return { x: hide.x + hide.dx * peek, y: hide.y + hide.dy * peek, peek };
}

/** Catches the ghost if it is far enough out and the tap is within its reach. */
function catchGhost(world: WorldState, tap: Tap, events: SimEvent[]): boolean {
	const ghost = world.ghost;
	const hide = ghost && HIDES[ghost.hide];
	if (!ghost || !hide) return false;
	const { x, y, peek } = ghostPosition(ghost, world.tick);
	if (peek < GHOST_CATCH_PEEK) return false;
	const dx = tap.x - x;
	const dy = tap.y - y;
	const reach = hide.r + tap.reach;
	if (dx * dx + dy * dy > reach * reach) return false;
	world.ghost = null;
	world.score += GHOST_POINTS;
	world.ghostsCaught++;
	events.push({ type: "ghostCaught", ghost, x, y, points: GHOST_POINTS, score: world.score });
	addToStreak(world, events);
	return true;
}

/** A tap that hit nothing: counted, breaks the streak, and with Minuspoäng costs a point (≥ 0). */
function miss(world: WorldState, tap: Tap, events: SimEvent[]): void {
	world.missed++;
	breakStreak(world, events);
	loseLife(world, events);
	if (!world.missPenalty) return;
	const penalty = Math.min(MISS_PENALTY, world.score);
	world.score -= penalty;
	events.push({ type: "missed", tap, penalty, score: world.score });
}

/** Switches on a random free spot; null when every spot is lit or resting. */
function switchOn(world: WorldState): Light | null {
	const free: number[] = [];
	world.restUntil.forEach((until, spot) => {
		if (until <= world.tick && !world.lights.some((l) => l.spot === spot)) free.push(spot);
	});
	if (free.length === 0) return null;
	const spot = free[nextInt(world.rng, free.length)] ?? 0;
	const light: Light = {
		id: world.nextId++,
		spot,
		color: nextInt(world.rng, LIGHT_COLOR_COUNT),
		golden: nextInt(world.rng, GOLDEN_ONE_IN) === 0,
		trick: false,
		litTick: world.tick,
		outTick: world.tick + lightLife(world),
	};
	// Trick lights only with Minuspoäng or Mardröm (no extra chance roll without it, so plain rounds play
	// exactly as before).
	if ((world.missPenalty || world.nightmare) && !light.golden)
		light.trick = nextInt(world.rng, TRICK_ONE_IN) === 0;
	world.lights.push(light);
	return light;
}
